/* eslint camelcase: 0 */
/* eslint-disable dot-notation */
import jwtDecode from 'jwt-decode';
//
import { handleError } from 'lib/helpers/error-handler';
import messages from 'constants/messages';
import { IAccessTokenPayload, IAccessTokenResponse } from 'lib/auth/types';
import { removeUserGroup } from 'lib/helpers/name-formatters';

// 14 minutes = 840,000 milliseconds
const MIN = 14;
const intervalSecConstValue = 60;
const intervalMiliSecConstValue = 1000;
const successResponseStatus = 200;
export const REFRESH_INTERVAL = MIN * intervalSecConstValue * intervalMiliSecConstValue;

const ADMIN_GROUP = 'fgr-sbobng-bngeco-zz-agilehub_dealanalyzer_admin-gs';

const windowEnv = window['ENV'];
let simassRedirectURI = '';
let simassClientId = '';
let simassAuthorizationEndpoint = '';
let CodeVerifierval = '';
let codeChallengeVal = '';
let simassTokenEndpoint = '';
// Added below lines of code to enable app initialization to fix redirection issue - INC3229025

if (localStorage.getItem('user_requested_url') === null) {
  localStorage.setItem('user_requested_url', window.location.pathname);
}
if (windowEnv) {
  simassRedirectURI = `${windowEnv['REACT_APP_REDIRECTURI']}`;
  simassClientId = `${windowEnv['REACT_APP_CLIENTID']}`;
  simassAuthorizationEndpoint = `${windowEnv['REACT_APP_AUTHORIZATIONENDPOINT']}`;
  CodeVerifierval = `${windowEnv['REACT_APP_CODEVERIFIERVAL']}`;
  codeChallengeVal = `${windowEnv['REACT_APP_CODECHALLENGEVAL']}`;
  simassTokenEndpoint = `${windowEnv['REACT_APP_TOKENENDPOINT']}`;
} else {
  simassRedirectURI = process.env.REACT_APP_REDIRECTURI || '';
  simassClientId = process.env.REACT_APP_CLIENTID || '';
  simassAuthorizationEndpoint = process.env.REACT_APP_AUTHORIZATIONENDPOINT || '';
  CodeVerifierval = process.env.REACT_APP_CODEVERIFIERVAL || '';
  codeChallengeVal = process.env.REACT_APP_CODECHALLENGEVAL || '';
  simassTokenEndpoint = process.env.REACT_APP_TOKENENDPOINT || '';
}

class AuthClient {
  private authorizationUrl?: string | null;

  private accessTokenResponse?: IAccessTokenResponse | null;

  private accessTokenPayload?: IAccessTokenPayload | null;

  private authorized: boolean = false;

  constructor() {
    this.fetchAuthorizationUrl();
    this.startRefreshInterval();
  }

  public startRefreshInterval = (callback?: Function) => {
    setInterval(() => {
      this.attemptRefreshAccessToken();
      return callback?.();
    }, REFRESH_INTERVAL);
  };

  public attemptRefreshAccessToken() {
    if (!this.isAuthorized()) {
      return;
    }

    this.refreshAccessToken();
  }

  public refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      handleError(messages.errors.noRefreshToken);
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
    const urlencoded = new URLSearchParams();
    urlencoded.append('grant_type', 'refresh_token');
    urlencoded.append('response_type', 'code');
    urlencoded.append('client_id', simassClientId);
    urlencoded.append('redirect_uri', simassRedirectURI);
    urlencoded.append('scope', 'openid');
    urlencoded.append('refresh_token', refreshToken);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: urlencoded,
    };

    fetch(simassTokenEndpoint, requestOptions)
      .then(response => response.json())
      .then(result => {
        this.accessTokenResponse = result;
        this.accessTokenPayload = this.decodeJwt();
      })
      .catch(error => handleError(`Error ${error}`));
  }

  public setAuthorizationUrl(authorizationUrl: string): void {
    this.authorizationUrl = authorizationUrl;
  }

  public setAccessTokenResponse(accessTokenResponse: IAccessTokenResponse): void {
    this.accessTokenResponse = accessTokenResponse;
  }

  public setAuthorized(authorized: boolean): void {
    this.authorized = authorized;
  }

  public getAuthorizationUrl(): string | undefined {
    return this.authorizationUrl ?? undefined;
  }

  public getAccessTokenResponse(): IAccessTokenResponse | undefined {
    return this.accessTokenResponse ?? undefined;
  }

  public isAuthorized(): boolean {
    this.setAuthorized(!!this.accessTokenResponse);
    return !!this.accessTokenResponse;
  }

  public getAccessToken(): string | undefined {
    return this.accessTokenResponse ? this.accessTokenResponse.access_token : undefined;
  }

  public getTokenPayload(): IAccessTokenPayload | undefined {
    return this.accessTokenPayload ? this.accessTokenPayload : undefined;
  }

  public getRefreshToken(): string | null {
    if (!this.accessTokenResponse) {
      return null;
    }
    if (this.accessTokenResponse) {
      return this.accessTokenResponse.refresh_token;
    }
    return null;
  }

  public isTokenExpired(): boolean {
    const tokenPayload: IAccessTokenPayload | undefined = this.decodeJwt();

    if (!tokenPayload) {
      return true;
    }

    const currentTime = new Date().getTime() / intervalMiliSecConstValue;

    if (currentTime > tokenPayload.exp) {
      return true;
    }

    return false;
  }

  public fetchUserAuthorization(): boolean {
    return this.isAuthorized();
  }

  public fetchAuthorizationUrl(): string {
    const scope = 'openid';
    const state = 'Yp3s5v8y';
    const responseType = 'code';
    const URL = `${simassAuthorizationEndpoint}?client_id=${simassClientId}&response_type=${responseType}&redirect_uri=${simassRedirectURI}&code_challenge=${codeChallengeVal}&code_challenge_method=S256&scope=${scope}&state=${state}`;
    this.authorizationUrl = URL;
    return URL;
  }

  public async fetchAccessTokenResponse(code: string) {
    const myHeaders = new Headers();
    myHeaders.append('content-type', 'application/x-www-form-urlencoded; charset=UTF-8');
    const urlencoded = new URLSearchParams();
    urlencoded.append('grant_type', 'authorization_code');
    urlencoded.append('client_id', simassClientId);
    urlencoded.append('code_verifier', CodeVerifierval);
    urlencoded.append('code', code);
    urlencoded.append('redirect_uri', simassRedirectURI);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: urlencoded,
    };

    const payload = await fetch(simassTokenEndpoint, requestOptions)
      .then(result => {
        if (result.status === successResponseStatus && result.ok) {
          return result.json();
        }
        if (result.statusText) {
          throw new Error(result.statusText);
        } else {
          throw new Error(messages.errors.default);
        }
      })
      .catch(error => handleError(`Error ${error}`));

    if (payload) {
      this.accessTokenResponse = payload;
      this.accessTokenPayload = this.decodeJwt();
    }
  }

  public getUserName(): string | undefined {
    return this.accessTokenPayload
      ? removeUserGroup(`${this.accessTokenPayload.sn}, ${this.accessTokenPayload.givenName}`)
      : undefined;
  }

  public isAuthorizedUserAdmin(): boolean {
    let isAdmin = false;
    if (this.accessTokenPayload) {
      isAdmin = this.accessTokenPayload.isMemberOf.includes(ADMIN_GROUP);
    }
    return isAdmin;
  }

  public decodeJwt(): IAccessTokenPayload | undefined {
    if (!this.accessTokenResponse) {
      return undefined;
    }
    const { access_token } = this.accessTokenResponse;
    let token: IAccessTokenPayload | undefined = jwtDecode(access_token);
    if (token) {
      token = {
        ...token,
        name: `${token.sn}, ${token.givenName}`,
      };
    }
    return token;
  }

  public flush(): void {
    this.authorizationUrl = null;
    this.accessTokenResponse = null;
    this.accessTokenPayload = null;
    this.authorized = false;
  }
}

export default AuthClient;
